import { useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import React, { MutableRefObject, PropsWithChildren, useMemo, useRef } from 'react'
import { useContainerDimensions } from '../../utils/dimension'
import { Link } from '../link/link'
import {
  StyledCardDesignImageContainer,
  StyledCarouselItemContainer,
  StyledDate,
  StyledDescription,
  StyledImage,
  StyledLinkTitle,
  StyledTitle,
} from './styledCardDesignCarousel'

// Image
export type CardDesignImageProps = React.ComponentProps<typeof StyledImage> & {
  src?: string
}

export const CardDesignImage: React.FC<PropsWithChildren<CardDesignImageProps>> = ({ src, ...props }) => {
  const imgContWidth = useRef() as MutableRefObject<HTMLDivElement>
  const { width } = useContainerDimensions(imgContWidth)

  return (
    <StyledCardDesignImageContainer style={{ height: `${width / 1.55}px` }} ref={imgContWidth}>
      {src && (
        <StyledImage src={src} {...props} />
      )}
    </StyledCardDesignImageContainer>
  )
}

// Date string
export type CardDesignDateProps = React.ComponentProps<typeof StyledDate> & {
  //
}

export const CardDesignDate: React.FC<PropsWithChildren<CardDesignDateProps>> = ({ href, children, ...props }) => (
  <StyledDate {...props}>
    {children}
  </StyledDate>
)

// Link title
export type CardDesignTitleProps = React.ComponentProps<typeof StyledLinkTitle> & {
  titleVariant?: 'h3' | 'body1'
}

export const CardDesignTitle: React.FC<PropsWithChildren<CardDesignTitleProps>> = ({ titleVariant, children, ...props }) => (
  <StyledTitle variant={titleVariant} {...props}>
    {children}
  </StyledTitle>
)

// Description
export type CardDesignDescriptionProps = React.ComponentProps<typeof StyledDescription> & {

}

export const CardDesignDescription: React.FC<PropsWithChildren<CardDesignDescriptionProps>> = ({ href, children, ...props }) => (
  <StyledDescription {...props}>
    {children}
  </StyledDescription>
)

// Item container
export type CardDesignItemContainerProps = React.ComponentProps<typeof StyledCarouselItemContainer> & {
  href?: string
}

export const CardDesignItemContainer: React.FC<PropsWithChildren<CardDesignItemContainerProps>> = ({ href, ...props }) => {
  return (
    <StyledCarouselItemContainer {...props} container justifyContent="flex-start" alignItems="center">
      <StyledLinkTitle href={href} {...props}>
        {props.children}
      </StyledLinkTitle>
    </StyledCarouselItemContainer>
  )
}

export type CarouselItemProps = ({ image?: boolean; title?: boolean; date?: boolean; description?: boolean }) & (
  // Image
  | ({ image: true } & CardDesignImageProps)
  // Title
  | ({ title: true } & CardDesignTitleProps)
  // Date
  | ({ date: true } & CardDesignDateProps)
  // Description
  | ({ description: true } & CardDesignDescriptionProps)
  // Item container
  | CardDesignItemContainerProps
)

export const CardDesignItem: React.FC<PropsWithChildren<CarouselItemProps>> = ({ image, title, date, description, children, ...props }) => {
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'))

  const itemPosition = useMemo(() => {
    return !isDesktop ? 'center' : 'flex-start'
  }, [isDesktop])

  const titleVariant = useMemo(() => {
    return !isDesktop ? 'body1' : 'h3'
  }, [isDesktop])

  if (image) {
    return (
      <CardDesignImage {...props} />
    )
  }

  if (date) {
    return (
      <CardDesignDate {...props} variant="body1">
        {children}
      </CardDesignDate>
    )
  }

  if (title) {
    return (
      <CardDesignTitle {...props} titleVariant={titleVariant}>
        {children}
      </CardDesignTitle>
    )
  }

  if (description) {
    return (
      <CardDesignDescription {...props}>
        {children}
      </CardDesignDescription>
    )
  }

  return (
    <CardDesignItemContainer {...props}
      container
      alignItems={itemPosition}
      justifyContent={itemPosition}>
      {children}
    </CardDesignItemContainer>
  )
}